// src/modules/pedidos/pages/RecuentoDetalle.js
import React, { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { guardarItems, marcarTerminado, confirmarRecuento, reabrirRecuento, asignarRecuento, totalItem } from "../services/recuentosFS";

// Contador con botones − / + (para sumar paquetes a medida que se cuenta)
function Stepper({ value, onChange, min = 0 }) {
  const v = Number(value) || 0;
  const btn = { width: "40px", height: "40px", borderRadius: "10px", border: "none", fontSize: "1.4rem", fontWeight: 700, cursor: "pointer", lineHeight: 1, flexShrink: 0 };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <button type="button" onClick={() => onChange(Math.max(min, v - 1))} style={{ ...btn, background: "#f1f5f9", color: "#334155" }}>−</button>
      <span style={{ minWidth: "30px", textAlign: "center", fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{v}</span>
      <button type="button" onClick={() => onChange(v + 1)} style={{ ...btn, background: "#2563eb", color: "#fff" }}>+</button>
    </div>
  );
}

// Agrupa items por "serie · color" (preservando orden de aparición)
function agruparPorCat(items) {
  const map = new Map();
  items.forEach(it => {
    const k = `${it.serie || "—"} · ${it.color || "—"}`;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  });
  return [...map.entries()];
}

export default function RecuentoDetalle() {
  const { id } = useParams();
  const { profile, user } = useAuth();
  const navigate = useNavigate();

  const [recuento, setRecuento] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState("");
  const [operarios, setOperarios] = useState([]);
  const [showResumen, setShowResumen] = useState(false); // resumen antes de terminar
  const [completando, setCompletando] = useState(false); // ventas/encargado transcribiendo la hoja
  const dirtyRef = useRef(false);

  const rol = (profile?.rol || "").toLowerCase();
  const puedeAsignar = rol === "encargado" || rol === "admin";

  // Operarios para asignar (robusto: soporta campo rol o role)
  useEffect(() => {
    if (!puedeAsignar) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const dep = profile?.deposito || null;
        setOperarios(snap.docs.map(d => ({ uid: d.id, ...d.data() }))
          .filter(u => {
            if (!hasRol(u, "operario", "encargado")) return false;
            if (dep && rol !== "admin" && u.deposito && u.deposito !== dep) return false;
            return true;
          }));
      } catch (e) { console.error("[operarios]", e); }
    })();
  }, [puedeAsignar, profile?.deposito, rol]);

  async function cambiarAsignado(uid) {
    const op = operarios.find(o => o.uid === uid);
    try {
      await asignarRecuento(id, op ? { uid: op.uid, nombre: op.nombre || op.email || "Operario" } : null);
      notar(op ? `Asignado a ${op.nombre || op.email}` : "Asignación quitada");
    } catch (e) { notar("No se pudo asignar"); console.error(e); }
  }

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "recuentos", id), snap => {
      if (!snap.exists()) { setLoading(false); return; }
      const data = { id: snap.id, ...snap.data() };
      setRecuento(data);
      // No pisar ediciones locales en curso
      if (!dirtyRef.current) {
        setItems((data.items || []).map(it => ({
          ...it,
          grupos: (it.grupos && it.grupos.length > 0)
            ? it.grupos
            : [{ paquetes: 0, tirasPorPaquete: it.tirasPorPaquete || 0 }],
        })));
      }
      setLoading(false);
    }, err => { console.error("[recuento]", err); setLoading(false); });
    return () => unsub();
  }, [id]);

  function notar(msg) { setToast(msg); setTimeout(() => setToast(""), 2500); }

  function setItem(idx, patch) {
    dirtyRef.current = true;
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }
  function setGrupo(idx, gi, patch) {
    setItem(idx, { grupos: items[idx].grupos.map((g, j) => j === gi ? { ...g, ...patch } : g) });
  }
  function addGrupo(idx) {
    setItem(idx, { grupos: [...items[idx].grupos, { paquetes: 0, tirasPorPaquete: items[idx].tirasPorPaquete || 0 }] });
  }
  function delGrupo(idx, gi) {
    setItem(idx, { grupos: items[idx].grupos.filter((_, j) => j !== gi) });
  }

  function itemsParaGuardar(extraIdx, extraPatch) {
    return items.map((it, i) => {
      const base = (extraIdx === i) ? { ...it, ...extraPatch } : it;
      return { ...base, total: totalItem(base) };
    });
  }

  const bumpEstado = () => (recuento?.estado === "ASIGNADO" ? "EN_CURSO" : undefined);

  async function confirmarItem(idx) {
    const patch = { contado: true, contadoPorNombre: profile?.nombre || profile?.email || "—", contadoAt: new Date().toISOString() };
    const nuevos = itemsParaGuardar(idx, patch);
    setItems(nuevos.map(it => ({ ...it, grupos: it.grupos && it.grupos.length ? it.grupos : [{ paquetes: 0, tirasPorPaquete: it.tirasPorPaquete || 0 }] })));
    try {
      setGuardando(true);
      await guardarItems(id, nuevos, bumpEstado());
      dirtyRef.current = false;
      notar("✓ Guardado");
    } catch (e) { notar("Error al guardar"); console.error(e); }
    finally { setGuardando(false); }
  }

  async function guardarTodo() {
    try {
      setGuardando(true);
      await guardarItems(id, itemsParaGuardar(), bumpEstado());
      dirtyRef.current = false;
      notar("✓ Avance guardado");
    } catch (e) { notar("Error al guardar"); console.error(e); }
    finally { setGuardando(false); }
  }

  async function doTerminar() {
    setShowResumen(false);
    try {
      setGuardando(true);
      await marcarTerminado(id, itemsParaGuardar(), profile);
      dirtyRef.current = false;
      notar("Recuento terminado");
    } catch (e) { notar("Error al terminar"); console.error(e); }
    finally { setGuardando(false); }
  }

  function exportarExcel() {
    const rows = items.map(it => ({
      "Codigo Finnegans": it.codigo || it.mold || "",
      "Producto": it.nombre || "",
      "Serie": it.serie || "",
      "Color": it.color || "",
      "Tiras x paquete (std)": it.tirasPorPaquete || "",
      "Contado (tiras)": totalItem(it),
      "Stock Finnegans": "",
      "Diferencia": "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 16 }, { wch: 26 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recuento");
    const safe = String(recuento.nombre || "recuento").replace(/[^\w\-]+/g, "_").slice(0, 40);
    XLSX.writeFile(wb, `Recuento_${safe}.xlsx`);
  }

  async function confirmar() {
    try {
      setGuardando(true);
      await confirmarRecuento(id, profile);
      notar("Recuento confirmado ✓");
    } catch (e) { notar("Error al confirmar"); console.error(e); }
    finally { setGuardando(false); }
  }

  async function reabrir() {
    try {
      setGuardando(true);
      await reabrirRecuento(id);
      notar("Recuento reabierto");
    } catch (e) { notar("Error al reabrir"); console.error(e); }
    finally { setGuardando(false); }
  }

  if (loading) return <div className="container screen-center">⏳ Cargando…</div>;
  if (!recuento) return <div className="container" style={{ padding: 20 }}>Recuento no encontrado. <button className="btn btn--ghost" onClick={() => navigate("/recuentos")}>Volver</button></div>;

  const contados = items.filter(it => it.contado).length;
  const grupos = agruparPorCat(items);

  const estado = recuento.estado || "ASIGNADO";
  const editable = estado === "ASIGNADO" || estado === "EN_CURSO";
  const esVentas = rol === "ventas";
  const esAsignado = recuento.asignadoAUid && recuento.asignadoAUid === user?.uid;
  const asignadoAOtro = recuento.asignadoAUid && recuento.asignadoAUid !== user?.uid;
  // Muestra los contadores: el responsable asignado; el encargado/admin si NO hay
  // nadie asignado; o cualquiera (ventas/encargado) que tocó "Completar recuento".
  const mostrarConteo = editable && (esAsignado || (puedeAsignar && !recuento.asignadoAUid) || completando);
  // Botón "Completar recuento": ventas o encargado/admin que no están contando por defecto.
  const puedeCompletar = editable && !mostrarConteo && (esVentas || puedeAsignar);
  const ESTADO_TXT = { ASIGNADO: "Asignado", EN_CURSO: "Realizando recuento", TERMINADO: "Recuento terminado", CONFIRMADO: "Confirmado" };

  return (
    <div className="container" style={{ paddingBottom: "90px", maxWidth: "720px" }}>

      {toast && <div className="toast" style={{ background: "var(--ok)" }}>{toast}</div>}

      {/* Header compacto */}
      <header className="topbar card" style={{ marginBottom: "12px", padding: "9px 12px" }} id="rec-header">
        <button onClick={() => navigate("/recuentos")} className="btn btn--ghost btn-sm" style={{ flexShrink: 0, padding: "6px 10px" }}>←</button>
        <div style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
          <h1 style={{ margin: 0, fontSize: "14px", fontWeight: 800, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{recuento.nombre}</h1>
          <p style={{ margin: "1px 0 0", fontSize: "11px", color: "#64748b" }}>
            {recuento.deposito || ""} · {contados}/{items.length} · {ESTADO_TXT[estado] || estado}
          </p>
        </div>
        <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
          <button onClick={exportarExcel} className="btn btn--ghost btn-sm" title="Exportar a Excel" style={{ padding: "6px 9px" }}>⬇️</button>
          <button onClick={() => window.print()} className="btn btn--ghost btn-sm" title="Imprimir hoja" style={{ padding: "6px 9px" }}>🖨️</button>
        </div>
      </header>

      {/* Asignación */}
      <div id="rec-asignacion" style={{ marginBottom: "14px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "11px 14px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 600 }}>👤 Responsable:</span>
        {puedeAsignar && estado !== "CONFIRMADO" ? (
          <select className="input" value={recuento.asignadoAUid || ""} onChange={e => cambiarAsignado(e.target.value)}
            style={{ flex: 1, minWidth: "180px", padding: "7px 10px" }}>
            <option value="">— Sin asignar (lo cuento yo) —</option>
            {recuento.asignadoAUid && !operarios.some(o => o.uid === recuento.asignadoAUid) && (
              <option value={recuento.asignadoAUid}>{recuento.asignadoANombre || "Asignado"}</option>
            )}
            {operarios.map(o => (
              <option key={o.uid} value={o.uid}>{o.nombre || o.email}{hasRol(o, "encargado") ? " (encargado)" : ""}</option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" }}>{recuento.asignadoANombre || "Sin asignar"}</span>
        )}
      </div>

      {/* Hint para ventas: imprimir y luego completar */}
      {esVentas && !completando && editable && (
        <div id="rec-hint-ventas" style={{ marginBottom: "14px", background: "#eff6ff", border: "1.5px solid #bfdbfe", borderRadius: "12px", padding: "12px 15px", fontSize: "0.83rem", color: "#1e40af" }}>
          🖨️ Imprimí la <strong>hoja de conteo</strong> (botón de arriba), contá en papel, y después tocá <strong>“Completar recuento”</strong> para pasar los números.
        </div>
      )}

      {/* Banner cuando el recuento lo hace otra persona (vista del encargado/admin) */}
      {asignadoAOtro && (
        <div id="rec-banner" style={{ marginBottom: "14px", background: estado === "TERMINADO" ? "#f3e8ff" : "#eff6ff", border: `1.5px solid ${estado === "TERMINADO" ? "#d8b4fe" : "#bfdbfe"}`, borderRadius: "12px", padding: "13px 15px" }}>
          <p style={{ margin: 0, fontWeight: 800, fontSize: "0.92rem", color: estado === "TERMINADO" ? "#7c3aed" : "#1d4ed8" }}>
            📋 Recuento asignado a {recuento.asignadoANombre || "un operario"}
          </p>
          <p style={{ margin: "3px 0 0", fontSize: "0.82rem", color: "#475569" }}>
            {estado === "TERMINADO"
              ? "Terminó el recuento. Revisalo abajo y tocá “Confirmar recuento”."
              : estado === "CONFIRMADO"
                ? "Recuento confirmado."
                : `Está realizando el recuento (${contados}/${items.length} contados). Cuando termine, lo vas a poder confirmar.`}
          </p>
        </div>
      )}

      {/* ▼▼ PANTALLA (no se imprime) ▼▼ */}
      <div id="rec-screen">
        {grupos.map(([cat, lista]) => (
          <div key={cat} style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 0 8px", padding: "6px 10px", background: "#eff6ff", borderRadius: "8px" }}>
              <span style={{ fontWeight: 800, fontSize: "0.82rem", color: "#185fa5" }}>🎨 {cat}</span>
              <span style={{ fontSize: "0.7rem", color: "#64748b" }}>{lista.length} producto{lista.length !== 1 ? "s" : ""}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {lista.map(it => {
                const idx = items.indexOf(it);
                const total = totalItem(it);
                return (
                  <div key={idx} className="card" style={{ padding: "10px 12px", borderLeft: `4px solid ${it.contado ? "#16a34a" : "#cbd5e1"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: mostrarConteo ? "8px" : "0" }}>
                      <span style={{ fontWeight: 800, fontSize: "0.88rem", color: "#0f172a", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.codigo || it.mold}{it.nombre ? ` · ${it.nombre}` : ""}</span>
                      {it.contado && <span style={{ flexShrink: 0, fontSize: "0.66rem", fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "2px 8px", borderRadius: "999px" }}>✓ {total}</span>}
                    </div>

                    {mostrarConteo ? (
                      <>
                        {/* Grupos de paquetes: [−] N [+] × tiras = subtotal */}
                        {it.grupos.map((g, gi) => (
                          <div key={gi} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
                            <Stepper value={g.paquetes} onChange={v => setGrupo(idx, gi, { paquetes: v })} />
                            <span style={{ color: "#94a3b8", fontSize: "0.9rem" }}>paq. ×</span>
                            <input type="number" min="0" value={g.tirasPorPaquete} onChange={e => setGrupo(idx, gi, { tirasPorPaquete: Math.max(0, Number(e.target.value)) })}
                              style={{ width: "46px", padding: "6px 4px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center", fontSize: "0.85rem" }} />
                            <span style={{ marginLeft: "auto", fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>{(Number(g.paquetes) || 0) * (Number(g.tirasPorPaquete) || 0)}</span>
                            {it.grupos.length > 1 && <button onClick={() => delGrupo(idx, gi)} style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: "1rem", padding: "0 2px" }}>🗑</button>}
                          </div>
                        ))}
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                          <button onClick={() => addGrupo(idx)} style={{ fontSize: "0.72rem", background: "none", border: "1px dashed #cbd5e1", borderRadius: "7px", padding: "5px 9px", cursor: "pointer", color: "#64748b" }}>+ otra medida de paquete</button>
                          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "0.78rem", color: "#64748b" }}>Sueltas</span>
                            <input type="number" min="0" value={it.sueltas} onChange={e => setItem(idx, { sueltas: Math.max(0, Number(e.target.value)) })}
                              style={{ width: "52px", padding: "6px 4px", borderRadius: "8px", border: "1px solid #e2e8f0", textAlign: "center", fontSize: "0.85rem" }} />
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontWeight: 800, fontSize: "1rem", color: "#0f6e56" }}>Total: {total}</span>
                          <button onClick={() => confirmarItem(idx)} disabled={guardando}
                            style={{ marginLeft: "auto", padding: "8px 16px", borderRadius: "9px", border: "none", background: it.contado ? "#0f766e" : "#0f172a", color: "#fff", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}>
                            {it.contado ? "✓ Actualizar" : "Confirmar"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginTop: "4px" }}>
                        <span style={{ color: "#64748b" }}>{it.contado ? "Contado" : "Sin contar"}</span>
                        <span style={{ fontWeight: 800, color: "#0f172a" }}>{total} tiras</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ▼▼ HOJA DE CONTEO (solo impresión) ▼▼ */}
      <div id="hoja-conteo">
        <h2 style={{ fontSize: "16px", margin: "0 0 2px" }}>{recuento.nombre}</h2>
        <p style={{ fontSize: "12px", margin: "0 0 4px", color: "#444" }}>
          Depósito: {recuento.deposito || "—"} &nbsp;·&nbsp; Fecha: ____ / ____ / ______ &nbsp;·&nbsp; Contó: ______________________
        </p>
        {grupos.map(([cat, lista]) => (
          <div key={cat} style={{ marginBottom: "10px" }}>
            <div style={{ fontWeight: 700, fontSize: "13px", background: "#eee", padding: "4px 6px" }}>{cat}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr>
                  <th style={thP}>Código</th><th style={thP}>Std (t/paq)</th>
                  <th style={{ ...thP, width: "38%" }}>Palitos / marcas</th>
                  <th style={thP}>Nº paq</th><th style={thP}>Tiras x paq</th><th style={thP}>Sueltas</th><th style={thP}>Total</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((it, i) => (
                  <tr key={i} style={{ height: "46px" }}>
                    <td style={tdP}>{it.codigo || it.mold}</td>
                    <td style={{ ...tdP, textAlign: "center" }}>{it.tirasPorPaquete || ""}</td>
                    <td style={tdP}></td><td style={tdP}></td><td style={tdP}></td><td style={tdP}></td><td style={tdP}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* CTA fijo según estado */}
      <div className="cta-fija" style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: "#fff", borderTop: "1px solid #e2e8f0", display: "flex", gap: "10px", alignItems: "center" }}>
        {mostrarConteo && (
          <>
            <button onClick={guardarTodo} disabled={guardando} style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Guardar avance</button>
            <button onClick={() => setShowResumen(true)} disabled={guardando || contados === 0} style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", background: contados === 0 ? "#cbd5e1" : "#16a34a", color: "#fff", fontWeight: 700, cursor: contados === 0 ? "not-allowed" : "pointer" }}>Terminar recuento</button>
          </>
        )}

        {estado === "TERMINADO" && (
          puedeAsignar ? (
            <>
              <button onClick={reabrir} disabled={guardando} style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Devolver a recuento</button>
              <button onClick={confirmar} disabled={guardando} style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, cursor: "pointer" }}>✓ Confirmar recuento</button>
            </>
          ) : (
            <div style={{ flex: 1, textAlign: "center", fontSize: "0.85rem", color: "#7c3aed", fontWeight: 700 }}>✓ Terminado · esperando confirmación del encargado</div>
          )
        )}

        {estado === "CONFIRMADO" && (
          <>
            <div style={{ flex: 1, fontSize: "0.85rem", color: "#15803d", fontWeight: 700 }}>✓ Confirmado{recuento.confirmadoPorNombre ? ` por ${recuento.confirmadoPorNombre}` : ""}</div>
            {puedeAsignar && <button onClick={reabrir} disabled={guardando} style={{ flexShrink: 0, padding: "11px 16px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Reabrir</button>}
          </>
        )}

        {!mostrarConteo && estado !== "TERMINADO" && estado !== "CONFIRMADO" && (
          puedeCompletar ? (
            <>
              {asignadoAOtro && <div style={{ flex: 1, fontSize: "0.78rem", color: "#94a3b8" }}>Asignado a {recuento.asignadoANombre}</div>}
              <button onClick={() => setCompletando(true)} style={{ flex: asignadoAOtro ? "0 0 auto" : 1, padding: "13px 18px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}>📝 Completar recuento</button>
            </>
          ) : (
            <div style={{ flex: 1, textAlign: "center", fontSize: "0.85rem", color: "#94a3b8" }}>
              {asignadoAOtro ? `Esperando que ${recuento.asignadoANombre || "el operario"} termine el recuento…` : "Sin acciones disponibles"}
            </div>
          )
        )}
      </div>

      {/* Modal RESUMEN antes de terminar */}
      {showResumen && (
        <div onClick={() => setShowResumen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: "720px", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 18px 10px", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>Resumen del recuento</h3>
              <p style={{ margin: "3px 0 0", fontSize: "0.82rem", color: "#64748b" }}>
                Revisá lo contado antes de terminar. {contados < items.length ? `Quedan ${items.length - contados} sin contar.` : "Todos los productos contados."}
              </p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
              {agruparPorCat(items).map(([cat, lista]) => (
                <div key={cat} style={{ marginBottom: "12px" }}>
                  <p style={{ margin: "0 0 6px", fontSize: "0.72rem", fontWeight: 700, color: "#185fa5" }}>{cat}</p>
                  {lista.map((it, i) => {
                    const t = totalItem(it);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 0", borderBottom: "1px solid #f1f5f9", fontSize: "0.85rem" }}>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{it.codigo || it.mold}</span>
                        <span style={{ color: "#64748b", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.nombre}</span>
                        {!it.contado && <span style={{ flexShrink: 0, fontSize: "0.66rem", color: "#b45309", background: "#fef3c7", padding: "1px 7px", borderRadius: "999px" }}>sin contar</span>}
                        <span style={{ flexShrink: 0, fontWeight: 800, color: t > 0 ? "#0f6e56" : "#94a3b8", minWidth: "60px", textAlign: "right" }}>{t} tiras</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div style={{ padding: "12px 16px 20px", borderTop: "1px solid #e2e8f0", display: "flex", gap: "10px" }}>
              <button onClick={() => setShowResumen(false)} style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Seguir contando</button>
              <button onClick={doTerminar} disabled={guardando} style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, cursor: "pointer" }}>✓ Confirmar y terminar</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        #hoja-conteo { display: none; }
        @media print {
          body * { visibility: hidden; }
          #hoja-conteo, #hoja-conteo * { visibility: visible; }
          #hoja-conteo { display: block; position: absolute; left: 0; top: 0; width: 100%; padding: 8px; }
          #rec-screen, #rec-header, #rec-asignacion, #rec-banner, #rec-hint-ventas, .cta-fija { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const thP = { border: "1px solid #999", padding: "3px 4px", fontSize: "11px", textAlign: "left", background: "#f5f5f5" };
const tdP = { border: "1px solid #999", padding: "3px 4px" };
